import React, { useState } from 'react';

/**
 * Collapsible panel displaying session context SNN snapshot.
 * Shows active concepts, topic trajectory, and cluster groupings.
 * Purely visual — does not affect the main pipeline.
 */
function SessionContextPanel({ snapshot }) {
  const [collapsed, setCollapsed] = useState(false);

  if (!snapshot) return null;

  const {
    active_concepts = [],
    concept_clusters = [],
    topic_trajectory = [],
    total_spikes = 0,
    simulation_wall_ms = 0,
    turn_count = 0,
    node_count = 0,
    edge_count = 0,
  } = snapshot;

  return (
    <div className="session-context-panel">
      <div
        className="session-context-panel-header"
        onClick={() => setCollapsed(c => !c)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && setCollapsed(c => !c)}
      >
        <span className="session-context-panel-title">
          Session Context Engine
        </span>
        <span className="session-context-panel-stats">
          {turn_count} turns &middot; {node_count} nodes &middot; {edge_count} edges &middot; {total_spikes} selections &middot; {simulation_wall_ms.toFixed(1)}ms
        </span>
        <span className="session-context-panel-chevron">
          {collapsed ? '\u25B6' : '\u25BC'}
        </span>
      </div>

      {!collapsed && (
        <div className="session-context-panel-body">
          {/* Active Concepts */}
          {active_concepts.length > 0 && (
            <div className="session-context-section">
              <h4 className="session-context-section-title">Active Concepts</h4>
              <div className="session-context-concepts">
                {active_concepts.map((concept, idx) => {
                  const label = concept.label || concept[0] || concept;
                  const score = concept.score != null ? concept.score : (concept[1] != null ? concept[1] : 0);
                  const opacity = 0.4 + 0.6 * score;
                  return (
                    <span
                      key={idx}
                      className="session-context-concept-chip"
                      style={{ opacity }}
                      title={`Score: ${(score * 100).toFixed(0)}%`}
                    >
                      {label}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Concept Clusters */}
          {concept_clusters.length > 0 && (
            <div className="session-context-section">
              <h4 className="session-context-section-title">Concept Clusters</h4>
              <div className="session-context-clusters">
                {concept_clusters.map((cluster, idx) => (
                  <div key={idx} className="session-context-cluster">
                    {cluster.map((label, ci) => (
                      <span key={ci} className="session-context-cluster-item">{label}</span>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Topic Trajectory */}
          {topic_trajectory.length > 0 && (
            <div className="session-context-section">
              <h4 className="session-context-section-title">Topic Trajectory</h4>
              <div className="session-context-trajectory">
                {topic_trajectory.map((label, idx) => (
                  <React.Fragment key={idx}>
                    {idx > 0 && <span className="session-context-trajectory-arrow">&rarr;</span>}
                    <span className="session-context-trajectory-step">{label}</span>
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SessionContextPanel;
